// janus-engine/combat/start-plan.ts — R-117 slice L3a (Janus lane N5, 2026-10-01): the combat START's deterministic opening, decided in
// the browser. (The island, the initiative dice, the combat-begins triggers / contingencies and the first combatant's start-of-turn block
// are still the PHP engine's — R-117 / R-128, waiting on Session A2's executors; see plans/janus-combat-lifecycle-plan-2026-10-01.md.)
//
// Ported out of ft_start_combat_and_roll_initiative() (includes/functions/janus/encounter/start_encounter_combat.php), in PHP's order:
//   rest     — the per-member out-of-combat economy BEFORE the combat-begins triggers (was sync_out_of_combat_encounter_creature_turn_economy,
//              its last caller): N1's R-124 rule as-is (planOutOfCombatEconomy over the held encounter — it is out of combat at a start):
//              aps → aps_total / reaction → 1 unless a no_actions / no_reactions payload status holds it; only CHANGED columns.
//   pre      — pools reset BEFORE the combat-begins triggers: Orb Slots back to its baseline (ft_reset_orb_slot_capacity — Capacitor's
//              capacity is combat-scoped) and the Summoner Ether pin of the same out-of-combat rule. The Psion's Psi → 0 (owner
//              2026-10-16): it reads full out of combat (economy/out-of-combat.ts) and every fight starts it empty, to be channelled up.
//   opening  — pools set as initiative is rolled: Champion Tactics → 3 and Summoner Ether → max for a PLAYER caster carrying the class
//              mechanic with its active passive; every LAIR ability with a numeric max → max (ft_reset_lair_ability_charges_to_max).
// The plan covers every candidate row the page holds; the engine saves an op only for a row it enrolled in the new combat (the target
// row), exactly as the R-115 start refill does. Pure: rows + readers in, ops out.
import { REFILL_BUCKETS, type RefillOp } from '../charges/combat-refill.js';
import { abilityEntryId } from '../charges/charge-adjust.js';
import { planOutOfCombatEconomy } from '../economy/out-of-combat.js';
import { orbSlotBaseline, TACTICS_MECHANIC_ID, type ExitReaders } from './exit-plan.js';
import { ETHER_MECHANIC_ID, PSI_MECHANIC_ID, holdsActivePsi } from '../economy/out-of-combat.js';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const scalar = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);
const upper = (v: unknown): string => String(scalar(v) ?? '').trim().toUpperCase();
const int = (v: unknown): number => { const n = Number(scalar(v) ?? 0); return Number.isFinite(n) ? Math.trunc(n) : 0; };
const phpEmpty = (v: unknown): boolean => { const s = scalar(v); return s === undefined || s === null || s === '' || s === 0 || s === '0' || s === false; };
const phpNumeric = (v: unknown): boolean => typeof v === 'number' ? Number.isFinite(v) : typeof v === 'string' && /^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/.test(v);
const phpInt = (v: unknown): number => {
  const s = scalar(v);
  if (typeof s === 'number') return Number.isFinite(s) ? Math.trunc(s) : 0;
  const m = /^\s*([+-]?\d+)/.exec(String(s ?? ''));
  return m ? parseInt(m[1]!, 10) : 0;
};

export interface StartRestWrite { id: string; aps?: number; reaction?: number }
/** `missing` = every definition the page should fetch; `blocking` = the subset without which a pool write (pre / opening) cannot be decided —
 * the adapter refuses the start until they are held (a status the rest cannot read only defers that actor's rest, never blocks). */
export interface CombatStartOpening { rest: StartRestWrite[]; pre: RefillOp[]; opening: RefillOp[]; missing: string[]; blocking: string[] }

const ecOf = (row: Row): Row => asRow(row.encounter_creatures) ?? {};
const idOf = (row: Row): string => upper(ecOf(row).encounter_creature_id);
const itemType = (row: Row): string => String(scalar(ecOf(row).item_type) ?? scalar(asRow(row.items)?.item_type) ?? '').toLowerCase();
const entryActive = (entry: Row): boolean => !('passive_conditions_active' in entry) || !phpEmpty(entry.passive_conditions_active);

/** ability_entry_max_charges($entry, $default): charges.max, the flat max, the definition's ability_charges layer, else $default.
 * `undefined` = the definition is needed and not held. */
function entryMax(entry: unknown, readers: ExitReaders, fallback: unknown): unknown {
  if (typeof entry === 'string') entry = { item_id: entry };
  const e = asRow(entry);
  if (!e) return fallback;
  const charges = asRow(e.charges);
  if (charges && 'max' in charges) return charges.max;
  if ('max_ability_charges' in e) return e.max_ability_charges;
  const inline = asRow(e.item_arr);
  const def = inline && asRow(inline.item_layer_types) ? inline : readers.definition(abilityEntryId(e));
  if (!def) return undefined;
  const layer = scalar(readers.value(def, 'ability_charges'));
  return layer === null || layer === undefined ? fallback : layer;
}

/** gather_and_roll_main_participants()'s player caster: a living, public, uncontrolled player character. */
const playerCaster = (row: Row): boolean => itemType(row) === 'player_character' && int(ecOf(row).encounter_creature_dead) === 0
  && phpEmpty(ecOf(row).encounter_creature_private) && phpEmpty(ecOf(row).controller_encounter_creature_id);
/** …and its lair: a living, uncontrolled lair (a lair joins while private). */
const lair = (row: Row): boolean => itemType(row) === 'lair' && int(ecOf(row).encounter_creature_dead) === 0 && phpEmpty(ecOf(row).controller_encounter_creature_id);

/** Psi at the bell (owner 2026-10-16): every PLAYER CHARACTER holding an active Psi passive enters a combat with an EMPTY pool — the
 *  start's `pre` ops and, for a PC that JOINS an ongoing combat, the join's follow-up refill (websocket/combat-truth.ts). */
export function psiBellOps(rows: Row[], readers: Pick<ExitReaders, 'entries'>): RefillOp[] {
  // PLAYER characters only (the owner's ruling is about Psion players; the Tactics / Ether opening is player-scoped the same way) — a
  // GM's psionic creature keeps the full out-of-combat pool it walks in with.
  return rows.filter(row => idOf(row) && itemType(row) === 'player_character' && holdsActivePsi(row, readers))
    .map(row => ({ holder: idOf(row), item_id: PSI_MECHANIC_ID, to: 0 }));
}

/** The Ether pin could not be decided (its definition is not held) — a pool write, so it blocks like the others. */
const economyEtherMissing = (economy: ReturnType<typeof planOutOfCombatEconomy>): boolean => economy.skipped.some(s => s.reason === 'ether-definition-not-held');

export function planCombatStartOpening(encounter: Row | null, readers: ExitReaders): CombatStartOpening {
  const roster = (Array.isArray(encounter?.encounter_creatures_arr) ? encounter!.encounter_creatures_arr as unknown[] : []).map(asRow)
    .filter((r): r is Row => !!r && !!idOf(r) && int(ecOf(r).encounter_creature_deleted) === 0);
  const missing = new Set<string>();
  const blocking = new Set<string>();
  const plan: CombatStartOpening = { rest: [], pre: [], opening: [], missing: [], blocking: [] };
  // rest + the Ether pin — the out-of-combat economy as the start found it (the held encounter is out of combat; else nothing).
  const economy = planOutOfCombatEconomy(encounter, roster, { layerValue: readers.value, entries: readers.entries, definition: readers.definition }, roster);
  // The out-of-combat Psi pin is never saved at a start (the bell empties Psi), so its definition is never needed here.
  economy.missing.filter(id => id !== PSI_MECHANIC_ID).forEach(id => missing.add(id));
  for (const op of economy.ops) {
    if (op.values) {
      const w: StartRestWrite = { id: op.actorId };
      if (op.values.after.aps !== op.values.before.aps) w.aps = op.values.after.aps;
      if (op.values.after.reaction !== op.values.before.reaction) w.reaction = op.values.after.reaction;
      if ('aps' in w || 'reaction' in w) plan.rest.push(w);
    }
    if (op.ether) plan.pre.push({ holder: op.actorId, item_id: op.ether.itemId, to: op.ether.after });
    // op.psi (the out-of-combat Psi pin) is never saved here: the bell empties Psi below.
  }
  for (const row of roster) {
    const id = idOf(row);
    // Orb Slots baseline — reset before the combat-begins triggers (an interrupted prior fight must not leak its capacity).
    const orb = orbSlotBaseline(row, readers);
    if (orb === 'missing') { missing.add('DADEF475-1E69-4EF4-8B46-06F3FC2B450D'); blocking.add('DADEF475-1E69-4EF4-8B46-06F3FC2B450D'); }
    else if (orb) plan.pre.push({ holder: id, item_id: 'DADEF475-1E69-4EF4-8B46-06F3FC2B450D', to: orb.to });
    // Class-mechanic opening charges for a player caster: the class_mechanic entry + an ACTIVE passive of the same id.
    if (playerCaster(row)) {
      const mechanics = readers.entries(row, 'class_mechanic').map(abilityEntryId);
      const passives = readers.entries(row, 'passive_ability').map(asRow).filter((p): p is Row => !!p && entryActive(p));
      for (const mech of [TACTICS_MECHANIC_ID, ETHER_MECHANIC_ID]) {
        if (!mechanics.includes(mech)) continue;
        for (const passive of passives) {
          if (abilityEntryId(passive) !== mech) continue;
          const max = entryMax(passive, readers, mech === TACTICS_MECHANIC_ID ? 'NA' : 0);
          if (max === undefined) { missing.add(mech); blocking.add(mech); continue; } // unheld ≠ a resolved non-numeric max (Codex 2026-10-01)
          if (mech === TACTICS_MECHANIC_ID) {
            // change_charges(…, 3) clamps to a NUMERIC max only (an unresolved 'NA' max never clamps).
            const ceiling = phpNumeric(scalar(max)) ? Math.trunc(Number(scalar(max))) : null;
            plan.opening.push({ holder: id, item_id: mech, to: Math.max(0, ceiling === null ? 3 : Math.min(3, ceiling)) });
            continue;
          }
          if (max === undefined) { missing.add(mech); continue; }
          plan.opening.push({ holder: id, item_id: mech, to: Math.max(0, phpInt(max)) });
        }
      }
    }
    // A lair opens at FULL charges: every ability entry with a numeric max.
    if (lair(row)) {
      for (const bucket of REFILL_BUCKETS) {
        for (const value of readers.entries(row, bucket)) {
          const aid = abilityEntryId(value);
          if (!aid) continue;
          const max = entryMax(value, readers, 'NA');
          if (max === undefined) { missing.add(aid); blocking.add(aid); continue; }
          if (!phpNumeric(scalar(max))) continue;
          plan.opening.push({ holder: id, item_id: aid, to: Math.max(0, Math.trunc(Number(scalar(max)))) });
        }
      }
    }
  }
  // Psi starts every combat at 0 (owner 2026-10-16) — no max to read, so no definition is needed.
  plan.pre.push(...psiBellOps(roster, readers));
  if (economyEtherMissing(economy)) blocking.add(ETHER_MECHANIC_ID);
  plan.missing = [...missing];
  plan.blocking = [...blocking];
  return plan;
}
